import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { map, pluck } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Tables } from '../enum/tables.enum';

/* -------------------------------------------------------------------------- */
/*                              GRAPHQL QUERIES                               */
/* -------------------------------------------------------------------------- */

// Generic update by PK
const UPDATE_BY_PK = (table: string) => gql`
  mutation updateRecord($id: uuid!, $fields: ${table}_set_input!) {
    update_${table}_by_pk(pk_columns: { id: $id }, _set: $fields) {
      id
    }
  }
`;

// Generic delete by PK
const DELETE_BY_PK = (table: string) => gql`
  mutation deleteRecord($id: uuid!) {
    delete_${table}_by_pk(id: $id) {
      id
    }
  }
`;

// Generic insert by table
const INSERT_BY_PK = (table: string) => gql`
  mutation insertRecord($fields: ${table}_insert_input!) {
    insert_${table}_one(object: $fields) {
      id
    }
  }
`;

// Generic get by ID
const GET_BY_ID = (table: string, fields: string[]) => gql`
  query getRecordById($id: uuid!) {
    ${table}_by_pk(id: $id) {
      ${fields.join('\n')}
    }
  }
`;

// Generic delete many (by condition)
const DELETE_MANY = (table: string) => gql`
  mutation deleteMany($where: ${table}_bool_exp!) {
    delete_${table}(where: $where) {
      affected_rows
    }
  }
`;

// Generic count records (by condition)
const COUNT_RECORDS = (table: string) => gql`
  query countRecords($where: ${table}_bool_exp!) {
    ${table}_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

// Generic get community_id, status, and type by member_id + community_id
const GET_COMMUNITY_STATUS = (table: string) => gql`
  query getCommunityStatus($member_id: uuid!, $community_id: uuid!) {
    ${table}(
      where: { 
        member_id: { _eq: $member_id },
        community_id: { _eq: $community_id }
      },
      limit: 1
    ) {
      status
    }
  }
`;

// Generic get community_id, status, and type by member_id + community_id
const GET_PARTNERSHIP_STATUS = (table: string) => gql`
  query getPartnershipStatus($member_id: uuid!, $community_id: uuid!) {
    ${table}(
      where: { 
        member_id: { _eq: $member_id },
        community_id: { _eq: $community_id }
      },
      limit: 1
    ) {
      status
      type
    }
  }
`;

// Generic get by field
const GET_BY_FIELD = (table: string, field: string, fields: string[]) => gql`
  query getRecordByField($value: uuid!) {
    ${table}(where: { ${field}: { _eq: $value } }, limit: 1) {
      ${fields.join('\n')}
    }
  }
`;

const getMyCommunityData = gql`
  query getMyCommunityData($where: community_members_bool_exp!) {
    community_members(where: $where) {
      id
      community_name
      community_id
    }
  }
`;

const getMyCatalogueData = gql`
  query getMyCatalogueData($where: catalogues_bool_exp!) {
    catalogues(where: $where) {
      id
      title
      dental_supplier_id
      community_user_type
      thumbnail_image
      title
      catalogue_sub_category {
        name
      }
      dental_supplier {
        business_name
      }
    }
  }
`;
const getMyCourseData = gql`
  query getMyCourseData($where: courses_bool_exp!) {
    courses(where: $where) {
      id
      created_by_id
      community_user_type
      course_name
      course_banner_image
      presenters
      cpd_points
      type
    }
  }
`;

/* -------------------------------------------------------------------------- */
/*                              SERVICE CLASS                                 */
/* -------------------------------------------------------------------------- */

@Injectable({
  providedIn: 'root',
})
export class CommonServicesForAllModules {
  constructor(private apollo: Apollo) {}

  // Insert data
  InsertData(table: Tables, fields: Record<string, any>): Promise<any> {
    const mutation = INSERT_BY_PK(table);
    return this.apollo.mutate({ mutation, variables: { fields } }).toPromise();
  }

  // Update data
  UpdateData(
    table: Tables,
    id: string,
    fields: Record<string, any>
  ): Promise<any> {
    const mutation = UPDATE_BY_PK(table);
    return this.apollo
      .mutate({ mutation, variables: { id, fields } })
      .toPromise();
  }

  // Delete by ID
  DeleteById(table: Tables, id: string): Promise<any> {
    const mutation = DELETE_BY_PK(table);
    return this.apollo.mutate({ mutation, variables: { id } }).toPromise();
  }

  // Delete many (with condition)
  DeleteMany(table: Tables, where: Record<string, any>): Promise<any> {
    const mutation = DELETE_MANY(table);
    return this.apollo.mutate({ mutation, variables: { where } }).toPromise();
  }

  // Get by ID
  GetById(table: Tables, id: string, fields: string[]) {
    const query = GET_BY_ID(table, fields);
    return this.apollo
      .watchQuery({ query, variables: { id }, fetchPolicy: 'network-only' })
      .valueChanges.pipe(map((res: any) => res.data[`${table}_by_pk`]));
  }

  // Count records
  CountRecords(
    table: Tables,
    where: Record<string, any> = {}
  ): Observable<number> {
    const query = COUNT_RECORDS(table);
    return this.apollo
      .query({ query, variables: { where }, fetchPolicy: 'network-only' })
      .pipe(pluck('data', `${table}_aggregate`, 'aggregate', 'count'));
  }

  // Auto-detect community table and get community_id
  GetCommunityId(
    memberId: string,
    communityId: string,
    userType?: string
  ): Observable<any | null> {
    const table: string = Tables.Community_Members;
    const query = GET_COMMUNITY_STATUS(table);
    return this.apollo
      .watchQuery({
        query,
        variables: { member_id: memberId, community_id: communityId },
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(
        map((res: any) => {
          const records = res?.data?.[table] || [];
          return records.length > 0 ? records[0] : null;
        })
      );
  }

  // Get Paternship table and get community_id
  GetPartnershipId(
    memberId: string,
    communityId: string,
    userType?: string
  ): Observable<any | null> {
    const table: string = Tables.Partnership_Members;
    const query = GET_PARTNERSHIP_STATUS(table);
    return this.apollo
      .watchQuery({
        query,
        variables: { member_id: memberId, community_id: communityId },
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(
        map((res: any) => {
          const records = res?.data?.[table] || [];
          return records.length > 0 ? records[0] : null;
        })
      );
  }

  GetByField(table: Tables, field: string, value: string, fields: string[]) {
    const query = GET_BY_FIELD(table, field, fields);
    return this.apollo
      .watchQuery({
        query,
        variables: { value },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((res: any) => res.data?.[table]?.[0] ?? null));
  }

  getMyCommunityData(id: string) {
    const where: any = {
      member_id: { _eq: id },
      status: { _eq: 'APPROVED' },
    };
    return this.apollo
      .use('external')
      .subscribe({
        query: getMyCommunityData,
        variables: { where },
      })
      .pipe(pluck('data', 'community_members'));
  }

  getMyCatalogueData(id: string) {
    const where: any = {
      _and: [
        { dental_supplier_id: { _eq: id } },
        { community_user_type: { _eq: 'BOTH' } },
      ],
    };

    return this.apollo
      .use('external')
      .watchQuery({
        query: getMyCatalogueData,
        variables: { where },
      })
      .valueChanges.pipe(pluck('data', 'catalogues'));
  }

  getMyCourseData(id: string) {
    const where: any = {
      _and: [
        { created_by_id: { _eq: id } },
        { community_user_type: { _eq: 'BOTH' } },
      ],
    };

    return this.apollo
      .use('external')
      .watchQuery({
        query: getMyCourseData,
        variables: { where },
      })
      .valueChanges.pipe(pluck('data', 'courses'));
  }

  DeleteByCondition(table: Tables, where: any): Promise<any> {
    const mutation = gql`
    mutation DeleteByCondition($where: ${table}_bool_exp!) {
      delete_${table}(where: $where) {
        affected_rows
      }
    }
  `;
    return this.apollo
      .mutate({
        mutation,
        variables: { where },
      })
      .toPromise();
  }
}
